import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import _ from 'lodash';
import { ListFilter } from 'lucide-react';
import { StyledGameCard, StyledGameDateHeader } from 'modules/games/ui/components/games-list/games-list';
import { fromMatchOverlayState } from 'modules/devices/domain/constants';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from 'shared/analytics';
import { SUPPORT_URL } from 'shared/constants/links';
import { getDateLocale } from 'shared/i18n';
import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { CalendarGame } from 'modules/schedule/domain/interfaces/calendar';
import { getMatchResultPath } from 'modules/schedule/domain/utils/match-result-path';
import { setTournamentId } from 'modules/schedule/store';
import { fetchCalendar } from 'modules/schedule/store/actions';
import { selectFilter } from 'modules/schedule/store/selectors';
import DataPicker from 'modules/schedule/ui/components/data-picker';
import { useSchedule } from 'modules/schedule/ui/hooks/use-schedule';
import useFilterModal from './modals/filters';
import { ChampionshipHeader, FilterButton, GamesList, GamesWrapper, MainBlock } from './styles';
import { theme } from 'styles/theme/theme';

function Calendar() {
  const { t, i18n } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const navigate = useNavigate();
  const { info, isLoading: isLoadingOrganizer } = useOrganizer();
  const { isLoading: isLoadingSchedule, isLoaded: isLoadedSchedule, selectDay, currentDate, tournamentId } = useSchedule();
  const filter = useSelector(selectFilter);
  const { setHeaderContent } = useMainLayout();

  useLayoutEffect(() => {
    StepperMainTool.setByPath('stepper:main:manageTournament', true);
    trackEvent('Championship Management Opened', {});
  }, []);

  useEffect(() => {
    if (!info) {
      return;
    }
    if (isLoadedSchedule && info.id === tournamentId) {
      return;
    }
    dispatch(setTournamentId({ id: info.id }));
    dispatchWithToast(
      fetchCalendar({
        tournamentId: info.id,
        monthNum: currentDate.month,
        yearNum: currentDate.year,
      }),
      { error: t`Не удалось загрузить календарь` },
    );
    // `t` в зависимостях перезапрашивал бы календарь при смене языка
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate.month, currentDate.year, dispatch, dispatchWithToast, info, isLoadedSchedule, tournamentId]);

  const dateKey = useMemo(() => {
    return format(new Date(selectDay.date), 'dd MMMM, EEEEEE', { locale: getDateLocale(i18n.locale) });
  }, [selectDay.date, i18n.locale]);

  // Ссылка на табло больше не строится по матчу — она принадлежит устройству,
  // поэтому кнопка ведёт на «Устройства», где эту ссылку и копируют.
  const onShowGameTemplatesClick = useCallback(() => {
    navigate(Pages.DEVICES.INDEX, { state: fromMatchOverlayState });
  }, [navigate]);

  // Обработчик отдаём карточке, только если у матча есть страница: иначе кнопки нет вовсе.
  const getShowResultClick = useCallback(
    (game: CalendarGame) => {
      const path = getMatchResultPath(game);
      return path ? () => navigate(path) : undefined;
    },
    [navigate],
  );

  const matches = useMemo(() => {
    return _(selectDay.matches).groupBy('championship.name').value();
  }, [selectDay.matches]);

  const matchesByFilter = useMemo(() => {
    const championshipName = filter.championship;
    return championshipName === 'all' ? matches : matches[championshipName] ? { [championshipName]: matches[championshipName] } : {};
  }, [filter.championship, matches]);

  const { open, modal } = useFilterModal();

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Расписание</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  return (
    <>
      <MainBlock>
        <div className={'flex'} style={{ height: 279, gap: 30, justifyContent: 'space-between' }}>
          <DataPicker />
        </div>
        {!(isLoadingOrganizer || isLoadingSchedule) &&
          (Object.keys(matchesByFilter).length > 0 ? (
            <div className="mb-6 w-full last:mb-0" style={{ overflow: 'hidden' }}>
              <StyledGameDateHeader
                style={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <p>{dateKey}</p>
                <UDTippy content={<Trans>Фильтр</Trans>} placement={'top'}>
                  <FilterButton onClick={open}>
                    <ListFilter size={16} />
                  </FilterButton>
                </UDTippy>
              </StyledGameDateHeader>
              <GamesWrapper>
                {_.map(matchesByFilter, (value, key) => (
                  <GamesList>
                    <ChampionshipHeader className={'ChampionshipHeader'}>
                      <UDText type={'subhead'} style={{ fontWeight: 'bold' }}>
                        {key}
                      </UDText>
                    </ChampionshipHeader>
                    {value.map((game) => (
                      <StyledGameCard
                        key={game.id}
                        game={game}
                        championshipId={game.championship.id}
                        onShowTemplatesClick={onShowGameTemplatesClick}
                        onShowResultClick={getShowResultClick(game)}
                      />
                    ))}
                  </GamesList>
                ))}
              </GamesWrapper>
            </div>
          ) : (
            <div
              className={'flex-center w-full'}
              style={{
                flexDirection: 'column',
                backgroundColor: 'white',
                paddingBlock: 26,
                borderRadius: 8,
                height: 'fit-content',
                position: 'relative',
              }}
            >
              <FilterButton
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: theme.colors.SurfaceCultured10,
                }}
                onClick={open}
              >
                <ListFilter size={16} />
              </FilterButton>
              <UDText type={'callout'} className={'text-surfaceRomanSilver30'} style={{ fontSize: 15 }}>
                <Trans>На эти даты матчей нет.</Trans>
              </UDText>
              <UDText type={'caption-1'}>
                <a style={{ color: theme.colors.EditorAccentBlue }} target={'_blank'} href={SUPPORT_URL} rel="noreferrer">
                  <Trans>Сообщить об ошибке</Trans>
                </a>
              </UDText>
            </div>
          ))}
      </MainBlock>
      {modal}
    </>
  );
}

export default Calendar;
